<template>
  <div :class="['clara-message', `clara-message--${roleClass}`]">
    <div class="clara-message__bubble">
      <!-- _blank matters: an in-answer wa.me/mailto support link must not navigate the SPA away. -->
      <vue-markdown
        v-if="isAssistant"
        class="clara-message__content"
        :source="message.content"
        :anchor-attributes="{ target: '_blank', rel: 'noopener noreferrer' }"
      />
      <span v-else class="clara-message__content">{{ message.content }}</span>
    </div>

    <div v-if="isAssistant && sources.length" class="clara-message__sources">
      <span
        v-for="source in sources"
        :key="source"
        class="clara-message__source"
      >
        {{ sourceLabel(source) }}
      </span>
    </div>

    <!--
      The ask is rendered here, from the locale, rather than appended by the model: a prompt
      instruction only holds on the prod model some of the time, and its language would follow the
      language detector rather than the UI. It disappears once the answer has been rated, so it
      asks once instead of nagging.
    -->
    <div v-if="isAssistant" class="clara-message__feedback">
      <span v-if="!message.feedbackRating" class="clara-message__feedback-ask">
        {{ $t('adminTitles.askClara.feedbackPrompt') }}
      </span>
      <button
        :class="['clara-message__feedback-btn', { 'is-active': message.feedbackRating === 'UP' }]"
        @click="rate('UP')"
      >👍</button>
      <button
        :class="['clara-message__feedback-btn', { 'is-active': message.feedbackRating === 'DOWN' }]"
        @click="onDownClick"
      >👎</button>
    </div>

    <div v-if="showCommentBox" class="clara-message__comment">
      <textarea
        v-model="comment"
        class="clara-message__comment-input"
        rows="2"
        :placeholder="$t('adminTitles.askClara.feedbackComment')"
      />
      <button class="clara-message__comment-send" @click="submitComment">
        {{ $t('adminTitles.askClara.send') }}
      </button>
    </div>
  </div>
</template>

<script>
import VueMarkdown from 'vue-markdown';

export default {
  name: 'AskClaraMessage',
  components: {
    VueMarkdown,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showCommentBox: false,
      comment: '',
    };
  },
  computed: {
    isAssistant() {
      return this.message.role === 'ASSISTANT';
    },
    roleClass() {
      return this.isAssistant ? 'assistant' : 'user';
    },
    sources() {
      return this.message.sourcesUsed || [];
    },
  },
  methods: {
    sourceLabel(source) {
      const map = {
        DATA: this.$t('adminTitles.askClara.sources.data'),
        MANUAL: this.$t('adminTitles.askClara.sources.manual'),
        COMMON_SENSE: this.$t('adminTitles.askClara.sources.commonSense'),
      };
      return map[source] || source;
    },
    rate(rating) {
      this.$emit('feedback', { turnId: this.message.turnId, rating });
    },
    onDownClick() {
      this.showCommentBox = true;
      this.rate('DOWN');
    },
    submitComment() {
      this.$emit('feedback', {
        turnId: this.message.turnId,
        rating: 'DOWN',
        comment: this.comment.trim(),
      });
      this.showCommentBox = false;
      this.comment = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.clara-message {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  &--user {
    align-items: flex-end;
  }

  &--assistant {
    align-items: flex-start;
  }

  &__bubble {
    max-width: 85%;
    font-size: 14px;
    line-height: 20px;
    color: var(--solid-black-purple);
  }

  &--user &__bubble {
    background: #F1F1F4;
    border-radius: 16px;
    padding: 10px 16px;
  }

  &__content {
    word-break: break-word;

    ::v-deep p {
      margin: 0 0 8px;
    }

    ::v-deep p:last-child {
      margin-bottom: 0;
    }
  }

  &__sources {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  &__source {
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
    color: var(--solid-purple);
    background: var(--solid-light-purple, #F4F2FB);
    border-radius: 6px;
    padding: 2px 8px;
  }

  &__feedback {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  &__feedback-ask {
    font-size: 11px;
    line-height: 14px;
    color: var(--solid-grey, #8A8A9E);
    margin-right: 4px;
  }

  &__feedback-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    opacity: 0.5;
    padding: 2px 4px;
    border-radius: 6px;
    transition: opacity 0.15s ease, background 0.15s ease;

    &:hover {
      opacity: 1;
    }

    &.is-active {
      opacity: 1;
      background: var(--solid-light-purple, #F4F2FB);
    }
  }

  &__comment {
    margin-top: 8px;
    width: 85%;
  }

  &__comment-input {
    width: 100%;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    font-family: 'Inter', sans-serif;
    resize: none;
    outline: none;

    &:focus {
      border-color: var(--solid-purple);
    }
  }

  &__comment-send {
    margin-top: 6px;
    border: none;
    background: var(--solid-purple);
    color: #FFFFFF;
    border-radius: 8px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
}
</style>
